import React, { useState } from "react";
import Section from "@cs/components/src/Section";
import { useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import Spinner from "@cs/components/src/Spinner";
import Table from "@cs/components/src/Table";
import styled from "styled-components";
import { newPluginInstance } from "../configurePluginOrScript/utils";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@cs/components/src/Banner";
import Checkbox from "@cs/components/src/Checkbox";
import Badge from "@cs/components/src/Badge";
import Fuse from "fuse.js";
import Input from "@cs/components/src/Input";
import { Search } from "react-feather";

interface Props {
  onAdd: (pluginInstance: PluginInstanceItem) => void;
}

const PluginRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h1 {
    font-size: 17px;
    margin-top: 5px;
    margin-bottom: 3px;
  }

  span {
    font-size: 15px;
    color: var(--dark700);
    display: block;
    margin-bottom: 5px;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 0;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 10px;
  }
`;

const AddPluginList = (props: Props) => {
  const { onAdd } = props;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["configList", "plugin"],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: PluginConfigItem[] }>(
        "GET",
        `/api/config/plugin`
      );
      return response.data;
    },
  });
  const { systemDomainId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showDevelopment, setShowDevelopment] = useState(false);
  const [search, setSearch] = useState("");

  // const configQuery = useQuery({
  //   mutationFn: async ()
  // })

  let rows = data ?? [];
  if (search) {
    const fuse = new Fuse(rows ?? [], {
      keys: ["title", "description"],
      ignoreLocation: true,
      threshold: 0.2,
      shouldSort: false,
    });

    rows = fuse.search(search).map((result) => result.item);
  }

  return (
    <Section
      title="Add Plugin"
      buttons={
        <StyledCheckbox
          value={showDevelopment}
          onChange={setShowDevelopment}
          label="Show Development"
          inlineLabel
        />
      }
    >
      <Input
        value={search}
        onChange={setSearch}
        placeholder="Search"
        icon={Search}
        clearable
      />
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Banner error="Error loading" />
      ) : (
        <>
          <Table
            rows={rows
              .filter((pluginConfig) =>
                showDevelopment ? true : !pluginConfig.development
              )
              .map((pluginConfig) => ({
                id: pluginConfig.id,
                onClick: () => {
                  const pluginInstance = newPluginInstance(
                    systemDomainId ?? "",
                    pluginConfig.id
                  );
                  onAdd(pluginInstance);
                  queryClient.setQueryData(
                    ["pluginInstance", pluginInstance.id],
                    {
                      pluginConfig,
                      pluginInstance,
                    }
                  );
                  navigate(`../${pluginInstance.id}`);
                },
                element: () => (
                  <PluginRow>
                    <Flex>
                      <h1>{pluginConfig.title}</h1>
                      {pluginConfig.development && (
                        <Badge color="var(--orange100)" label="Development" />
                      )}
                    </Flex>
                    {pluginConfig.description && (
                      <span>{pluginConfig.description}</span>
                    )}
                  </PluginRow>
                ),
              }))}
            autoHeight={{ twoColumn: true }}
          />
        </>
      )}
    </Section>
  );
};

export default AddPluginList;
